import type { ReactNode } from 'react';
import classNames from 'classnames';

import CheckCircleFill from '@/assets/icons/check_circle_fill.svg?component';
import FishGroupLine from '@/assets/icons/fish_group_line.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { type IconComponent, SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import styles from './ConfirmRegistration.module.scss';

export interface ConfirmRegistrationItem {
    key: string;
    icon: IconComponent;
    title: string;
    text: string;
}

interface ConfirmRegistrationProps {
    /** The pens the fish leave, shown dashed. Left empty with `toPens`, no diagram is drawn. */
    fromPens: string[];
    /** The pens the fish end up in; empty when no fish move, as for a delousing. */
    toPens: string[];
    title: string;
    items: ConfirmRegistrationItem[];
    /** Something the customer should know before confirming, such as a stocking it joins. */
    warning?: ReactNode;
    error?: ReactNode;
    cancelLabel: string;
    confirmLabel: string;
    isSubmitting: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

function Glyph({ icon }: { icon: IconComponent }) {
    return (
        <SvgIcon
            icon={icon}
            size="inherit"
            svgProps={{ 'aria-hidden': true, focusable: 'false' }}
        />
    );
}

/**
 * The last step of a fish group action: where the fish go, what the board will show, and the
 * one button that registers it. There is no undo for a customer, so nothing is sent before it.
 */
export default function ConfirmRegistration(props: ConfirmRegistrationProps) {
    const { fromPens, toPens, title, items, warning, error } = props;
    const { cancelLabel, confirmLabel, isSubmitting, onCancel, onConfirm } = props;

    return (
        <div className={styles.confirm}>
            {toPens.length > 0 && (
                <div className={styles.diagram} aria-hidden="true">
                    <div className={styles.pens}>
                        {fromPens.map((pen) => (
                            <span key={pen} className={styles.pen}>
                                <span className={classNames(styles.disc, styles.discFrom)} />
                                <span className={styles.penName}>{pen}</span>
                            </span>
                        ))}
                    </div>
                    <span className={styles.link}>
                        <span className={styles.check}>
                            <Glyph icon={CheckCircleFill} />
                        </span>
                    </span>
                    <div className={styles.pens}>
                        {toPens.map((pen) => (
                            <span key={pen} className={styles.pen}>
                                <span className={classNames(styles.disc, styles.discTo)}>
                                    <Glyph icon={FishGroupLine} />
                                </span>
                                <span className={classNames(styles.penName, styles.penNameTo)}>
                                    {pen}
                                </span>
                            </span>
                        ))}
                    </div>
                </div>
            )}
            <h3 className={styles.title}>{title}</h3>
            <ul className={styles.items}>
                {items.map((item) => (
                    <li key={item.key} className={styles.item}>
                        <span className={styles.itemTitle}>
                            <span className={styles.itemIcon}>
                                <Glyph icon={item.icon} />
                            </span>
                            {item.title}
                        </span>
                        <span className={styles.itemText}>{item.text}</span>
                    </li>
                ))}
            </ul>
            {warning && (
                <Alert tone="warning" size="small">
                    {warning}
                </Alert>
            )}
            {error}
            <div className={styles.actions}>
                <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
                    {cancelLabel}
                </Button>
                <Button variant="primary" onClick={onConfirm} disabled={isSubmitting}>
                    {confirmLabel}
                </Button>
            </div>
        </div>
    );
}
